<template>
  <div>
    <!-- Hero Section -->
    <section class="relative min-h-[650px] flex items-center overflow-hidden">
      <!-- Background Image -->
      <div class="absolute inset-0">
        <div class="absolute inset-0 bg-gradient-to-r from-black/80 via-black/60 to-black/40">
          <img   loading="lazy" v-if="getdevice === 'mobile'" class="w-full h-full object-cover opacity-50" :src="heroBackground" alt="">
        </div>
      </div>

      <!-- Loading State -->
      <div v-if="loading" class="absolute inset-0 bg-black/50 z-20 flex items-center justify-center">
        <div class="text-white text-xl">Chargement...</div>
      </div>

      <!-- Content -->
      <div class="relative z-10 w-full max-w-7xl mx-auto px-4 py-12">
        <div class="grid grid-cols-1 lg:grid-cols-2 gap-8 lg:gap-12 items-center">

          <!-- Left Side - Slogan & CTA -->
          <div class="space-y-6 text-center lg:text-left">
            <h1 class="text-3xl md:text-4xl lg:text-5xl xl:text-6xl font-bold text-white leading-tight">
              Tout ce dont vous avez besoin,
              <span
                class="bg-gradient-to-r from-secondary to-secondary-hover bg-clip-text text-transparent block lg:inline">
                au meilleur prix
              </span>
            </h1>

            <p class="text-base md:text-lg text-gray-300 max-w-lg mx-auto lg:mx-0 leading-relaxed">
              Découvrez notre sélection exclusive de produits premium.
              Mode, électronique, beauté et plus encore — tout en un seul endroit.
            </p>

            <div class="flex flex-col sm:flex-row gap-4 justify-center lg:justify-start pt-2">
              <router-link to="/category">
                <button
                  class="bg-secondary hover:bg-secondary-hover text-secondary-foreground font-semibold px-8 py-6 text-lg shadow-glow w-full sm:w-auto rounded-lg transition-normal flex items-center justify-center">
                  Découvrir les produits
                  <ArrowRight class="ml-2 h-5 w-5" />
                </button>
              </router-link>

              <router-link to="/category/promos">
                <button
                  class="border border-white/30 text-white hover:bg-white/10 px-8 py-6 text-lg w-full sm:w-auto rounded-lg transition-normal flex items-center justify-center">
                  <Percent class="mr-2 h-5 w-5" />
                  Voir les promos
                </button>
              </router-link>
            </div>

            <!-- Stats -->
            <div class="flex items-center justify-center lg:justify-start gap-8 pt-6">
              <div class="text-center">
                <p class="text-2xl md:text-3xl font-bold text-white">{{ stats.customers }}+</p>
                <p class="text-sm text-gray-400">Clients satisfaits</p>
              </div>
              <div class="w-px h-12 bg-white/20"></div>
              <div class="text-center">
                <p class="text-2xl md:text-3xl font-bold text-white">{{ productnumber }}+</p>
                <p class="text-sm text-gray-400">Produits</p>
              </div>
              <div class="w-px h-12 bg-white/20"></div>
              <div class="text-center">
                <p class="text-2xl md:text-3xl font-bold text-white">24/7</p>
                <p class="text-sm text-gray-400">Support</p>
              </div>
            </div>
          </div>

          <!-- Right Side - Promo Products Slider -->
          <div v-show="getdevice !== 'mobile' && heroPromoProducts.length > 0" class="relative">
            <div class="flex items-center gap-4">
              <!-- Navigation Button - Previous -->
              <button @click="prevSlide" :disabled="heroPromoProducts.length <= visibleCards"
                class="flex-shrink-0 w-10 h-10 rounded-full bg-white/10 hover:bg-white/20 border border-white/20 flex items-center justify-center text-white transition-all duration-300 hover:scale-110 disabled:opacity-50 disabled:cursor-not-allowed"
                aria-label="Produit précédent">
                <ChevronLeft class="h-5 w-5" />
              </button>

              <!-- Cards Container -->
              <div class="flex-1 overflow-hidden">
                <div v-if="heroPromoProducts.length > 0" class="flex gap-4 transition-transform duration-500 ease-out"
                  :style="{ transform: 'translateX(-' + (currentSlide * (100 / visibleCards + 7)) + '%)' }">
                  <div v-for="product in heroPromoProducts" :key="product.id"
                    class="flex-shrink-0 md:w-[calc(50%)] lg:w-[calc(50%)] ms:w-[calc(50%)] group">
                    <router-link :to="`/product/${product.category_slug}/${product.slug}`">
                      <div
                        class="bg-white/10 backdrop-blur-md rounded-2xl overflow-hidden border border-white/20 hover:border-secondary/50 transition-all duration-300 hover:scale-[1.02] hover:shadow-xl">
                        <!-- Product Image -->
                        <div class="relative aspect-square overflow-hidden">
                          <img   loading="lazy" :src="getProductImage(product)" :alt="product.name"
                            class="w-full h-full object-cover group-hover:scale-110 transition-transform duration-500" />
                          <!-- Discount Badge -->
                          <div v-if="product.discount > 0"
                            class="absolute top-3 left-3 bg-red-500 text-white px-3 py-1 rounded-full text-sm font-bold shadow-lg">
                            -{{ product.discount }}%
                          </div>
                          <!-- Category Badge -->
                          <div v-if="product.category"
                            class="absolute top-3 right-3 bg-black/50 backdrop-blur-sm text-white px-3 py-1 rounded-full text-xs">
                            {{ product.category }}
                          </div>
                        </div>

                        <!-- Product Info -->
                        <div class="p-4 space-y-2">
                          <h3
                            class="text-white font-semibold text-sm md:text-base line-clamp-1 group-hover:text-secondary transition-colors">
                            {{ product.name }}
                          </h3>
                          <div class="flex items-center gap-2 flex-wrap">
                            <span class="text-secondary font-bold text-base md:text-lg">
                              {{ formatPrice(getPromoPrice(product)) }}
                            </span>
                            <span v-if="product.originalPrice" class="text-gray-400 text-sm line-through">
                              {{ formatPrice(product.originalPrice) }}
                            </span>
                          </div>
                        </div>
                      </div>
                    </router-link>
                  </div>
                </div>

                <!-- Empty State -->
                <div v-else class="text-center py-8">
                  <p class="text-white/70">Aucun produit en promotion pour le moment</p>
                </div>
              </div>

              <!-- Navigation Button - Next -->
              <button @click="nextSlide" :disabled="heroPromoProducts.length <= visibleCards"
                class="flex-shrink-0 w-10 h-10 rounded-full bg-white/10 hover:bg-white/20 border border-white/20 flex items-center justify-center text-white transition-all duration-300 hover:scale-110 disabled:opacity-50 disabled:cursor-not-allowed"
                aria-label="Produit suivant">
                <ChevronRight class="h-5 w-5" />
              </button>
            </div>

            <!-- Slide Indicators -->
            <div v-if="heroPromoProducts.length > visibleCards" class="flex justify-center gap-2 mt-6">
              <button v-for="index in slideIndicators" :key="index" @click="goToSlide(index - 1)" :class="[
                'h-2 rounded-full transition-all duration-300',
                currentSlide === index - 1
                  ? 'w-8 bg-secondary'
                  : 'w-2 bg-white/30 hover:bg-white/50'
              ]" :aria-label="'Aller au slide ' + index"></button>
            </div>

            <!-- Promo Label -->
            <div class="text-center mt-4">
              <span class="text-white/70 text-sm">
                Offres à durée limitée — Économisez jusqu'à <span class="text-secondary font-bold">50%</span>
              </span>
            </div>
          </div>
        </div>
      </div>

      <!-- Scroll Indicator -->
      <div class="absolute bottom-6 left-1/2 transform -translate-x-1/2 animate-bounce hidden md:block">
        <div class="w-6 h-10 border-2 border-white/50 rounded-full flex justify-center">
          <div class="w-1 h-3 bg-white/50 rounded-full mt-2"></div>
        </div>
      </div>
    </section>

    <!-- Categories Section -->
    <section class="py-16 bg-background">
      <div class="container mx-auto px-4">
        <div class="text-center mb-12">
          <h2 class="text-3xl md:text-4xl font-bold text-foreground mb-4">
            Nos Catégories
          </h2>
          <p class="text-lg text-muted-foreground">
            Explorez notre large sélection de produits
          </p>
        </div>

        <div v-if="categories.length > 0" class="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-8 gap-4">
          <router-link v-for="category in categories" :key="category.id" :to="'/category/' + category.slug">
            <div
              class="group hover:shadow-lg transition-all duration-300 border-0 shadow-sm overflow-hidden h-full bg-card rounded-lg">
              <div class="p-4 text-center">
                <div :class="[
                  'w-14 h-14 mx-auto mb-3 rounded-xl bg-gradient-to-br flex items-center justify-center group-hover:scale-110 transition-transform duration-300',
                  getCategoryColor(category)
                ]">
                  <component :is="getCategoryIcon(category)" class="h-7 w-7 text-white" />
                </div>
                <h3 class="text-sm font-semibold text-foreground group-hover:text-secondary transition-colors">
                  {{ category.name }}
                </h3>
                <p v-if="category.productCount" class="text-xs text-muted-foreground mt-1">
                  {{ category.productCount }} produits
                </p>
              </div>
            </div>
          </router-link>
        </div>

        <!-- Loading Categories -->
        <div v-else-if="loading" class="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-8 gap-4">
          <div v-for="n in 8" :key="n" class="animate-pulse">
            <div class="p-4 text-center bg-gray-200 rounded-lg h-32"></div>
          </div>
        </div>
      </div>
    </section>

    <!-- Trending Products -->
    <section v-if="trendingProducts.length > 0" class="py-16 bg-muted">
      <div class="container px-2">
        <div class="flex items-center justify-between mb-8">
          <div class="flex items-center gap-3">
            <TrendingUp class="h-8 w-8 text-orange-500" />
            <div>
              <h2 class="text-2xl md:text-3xl font-bold text-foreground">Tendances</h2>
              <p class="text-muted-foreground">Les produits les plus populaires du moment</p>
            </div>
          </div>
          <div class="hidden md:flex items-center gap-2">
            <button
              class="border border-input bg-background hover:bg-accent hover:text-accent-foreground h-10 w-10 flex items-center justify-center rounded-md transition-normal disabled:opacity-50 disabled:cursor-not-allowed"
              @click="prevTrendingIndex" :disabled="trendingIndex === 0">
              <ChevronLeft class="h-4 w-4" />
            </button>
            <button
              class="border border-input bg-background hover:bg-accent hover:text-accent-foreground h-10 w-10 flex items-center justify-center rounded-md transition-normal disabled:opacity-50 disabled:cursor-not-allowed"
              @click="nextTrendingIndex" :disabled="trendingIndex >= trendingProducts.length - 4">
              <ChevronRight class="h-4 w-4" />
            </button>
          </div>
        </div>

        <div v-if="trendingProducts.length > 0" class="grid grid-cols-2 sm:grid-cols-2 lg:grid-cols-4 gap-2">
          <div v-for="product in visibleTrendingProducts" :key="product.id"
            class="group hover:shadow-xl transition-all duration-300 border-0 shadow-md overflow-hidden bg-card rounded-md">
            <div class="relative">
              <router-link :to="`/product/${product.category_slug}/${product.slug}`">
                <div class="aspect-square overflow-hidden bg-muted">
                  <img   loading="lazy" :src="getProductImage(product)" :alt="product.name"
                    class="w-full h-full object-cover group-hover:scale-105 transition-transform duration-300"
                    @error="handleImageError" />
                </div>
              </router-link>

              <!-- Badges -->
              <div class="absolute top-3 left-3 flex flex-col gap-2">
                <span v-if="hasDiscount(product)"
                  class="bg-destructive text-destructive-foreground px-2 py-1 rounded-sm text-xs font-semibold">
                  -{{ calculateDiscount(product) }}%
                </span>
                <span v-if="getProductBadge(product)" :class="getBadgeClass(getProductBadge(product))">
                  {{ getProductBadge(product) }}
                </span>
              </div>

              <!-- Wishlist -->
              <button @click="toggleWishlist(product)"
                class="absolute top-3 right-3 w-8 h-8 bg-background/80 hover:bg-background rounded-full flex items-center justify-center transition-colors">
                <Heart :class="[
                  'h-4 w-4',
                  isInWishlist(product.id) ? 'fill-destructive text-destructive' : 'text-muted-foreground'
                ]" />
              </button>

              <!-- Quick Add -->
              <div
                class="absolute bottom-3 left-3 right-3 opacity-50 group-hover:opacity-100 transition-opacity duration-300">
                <button @click="addToCart(product)" :disabled="!product.in_stock || product.stock_quantity <= 0"
                  :class="[
                    'w-full py-2 rounded-lg transition-normal flex items-center justify-center',
                    product.in_stock && product.stock_quantity > 0
                      ? 'bg-primary hover:bg-primary-hover text-primary-foreground'
                      : 'bg-gray-400 cursor-not-allowed text-gray-700'
                  ]">
                  <ShoppingCart class="h-4 w-4 mr-2" />
                  {{ product.stock_quantity > 0 ? 'Ajouter au panier' : 'Rupture' }}
                </button>
              </div>
            </div>

            <div class="p-4">
              <span v-if="product.category" class="text-xs text-muted-foreground">
                {{ product.category }}
              </span>
              <router-link :to="`/product/${product.category_slug}/${product.slug}`">
                <h3 class="font-semibold text-foreground hover:text-secondary transition-colors mt-1 line-clamp-2">
                  {{ product.name }}
                </h3>
              </router-link>

              <!-- Rating -->
              <div class="flex items-center gap-1 mt-2" v-if="product.rating > 0">
                <div class="flex">
                  <Star v-for="i in 5" :key="i" :class="[
                    'h-3 w-3',
                    i <= Math.floor(product.rating)
                      ? 'fill-secondary text-secondary'
                      : 'text-muted-foreground/30'
                  ]" />
                </div>
                <span class="text-xs text-muted-foreground">
                  ({{ product.reviews_count || 0 }})
                </span>
              </div>

              <!-- Price -->
              <div class="flex items-center gap-2 mt-2">
                <span class="text-lg font-bold text-foreground">
                  {{ formatPrice(getProductPrice(product)) }}
                </span>
                <span v-if="hasDiscount(product)" class="text-sm text-muted-foreground line-through">
                  {{ formatPrice(product.price) }}
                </span>
              </div>

              <!-- Stock Status -->
              <div v-if="product.stock_quantity <= 10 && product.stock_quantity > 0" class="mt-1">
                <p class="text-xs text-amber-600">
                  Plus que {{ product.stock_quantity }} disponible{{ product.stock_quantity > 1 ? 's' : '' }}
                </p>
              </div>
            </div>
          </div>
        </div>

        <!-- Empty State -->
        <div v-else-if="!loading" class="text-center py-12">
          <Package class="h-16 w-16 text-muted-foreground mx-auto mb-4" />
          <h3 class="text-lg font-semibold text-muted-foreground mb-2">Aucun produit tendance pour le moment</h3>
          <router-link to="/category/trending">
            <button
              class="border border-input bg-background hover:bg-accent hover:text-accent-foreground px-4 py-2 rounded-md mt-2">
              Voir tous les produits
            </button>
          </router-link>
        </div>

        <!-- Loading State -->
        <div v-else class="grid grid-cols-2 sm:grid-cols-2 lg:grid-cols-4 gap-2">
          <div v-for="n in 4" :key="n" class="animate-pulse">
            <div class="bg-gray-200 rounded-md h-96"></div>
          </div>
        </div>

        <div v-if="trendingProducts.length > 0" class="text-center mt-8">
          <router-link to="/category/trending">
            <button
              class="border border-input bg-background hover:bg-accent hover:text-accent-foreground h-12 px-8 py-2 rounded-md text-lg transition-normal flex items-center justify-center mx-auto">
              Voir toutes les tendances
              <ArrowRight class="ml-2 h-4 w-4" />
            </button>
          </router-link>
        </div>
      </div>
    </section>

    <!-- Bestsellers -->
    <section v-if="bestsellerProducts.length > 0" class="py-16 bg-background">
      <div class="container px-2">
        <div class="flex items-center justify-between mb-8">
          <div class="flex items-center gap-3">
            <Star class="h-8 w-8 text-secondary fill-secondary" />
            <div>
              <h2 class="text-2xl md:text-3xl font-bold text-foreground">Meilleures Ventes</h2>
              <p class="text-muted-foreground">Nos produits les plus vendus</p>
            </div>
          </div>
          <div class="hidden md:flex items-center gap-2">
            <button
              class="border border-input bg-background hover:bg-accent hover:text-accent-foreground h-10 w-10 flex items-center justify-center rounded-md transition-normal disabled:opacity-50 disabled:cursor-not-allowed"
              @click="prevBestsellerIndex" :disabled="bestsellerIndex === 0">
              <ChevronLeft class="h-4 w-4" />
            </button>
            <button
              class="border border-input bg-background hover:bg-accent hover:text-accent-foreground h-10 w-10 flex items-center justify-center rounded-md transition-normal disabled:opacity-50 disabled:cursor-not-allowed"
              @click="nextBestsellerIndex" :disabled="bestsellerIndex >= bestsellerProducts.length - 4">
              <ChevronRight class="h-4 w-4" />
            </button>
          </div>
        </div>

        <div v-if="bestsellerProducts.length > 0" class="grid grid-cols-2 sm:grid-cols-2 lg:grid-cols-4 gap-2">
          <div v-for="product in visibleBestsellerProducts" :key="product.id"
            class="group hover:shadow-xl transition-all duration-300 border-0 shadow-md overflow-hidden bg-card rounded-md">
            <div class="relative">
              <router-link :to="`/product/${product.category_slug}/${product.slug}`">
                <div class="aspect-square overflow-hidden bg-muted">
                  <img   loading="lazy" :src="getProductImage(product)" :alt="product.name"
                    class="w-full h-full object-cover group-hover:scale-105 transition-transform duration-300"
                    @error="handleImageError" />
                </div>
              </router-link>

              <!-- Badges -->
              <div class="absolute top-3 left-3 flex flex-col gap-2">
                <span v-if="hasDiscount(product)"
                  class="bg-destructive text-destructive-foreground px-2 py-1 rounded-sm text-xs font-semibold">
                  -{{ calculateDiscount(product) }}%
                </span>
                <span v-if="getProductBadge(product)" :class="getBadgeClass(getProductBadge(product))">
                  {{ getProductBadge(product) }}
                </span>
              </div>

              <!-- Wishlist -->
              <button @click="toggleWishlist(product)"
                class="absolute top-3 right-3 w-8 h-8 bg-background/80 hover:bg-background rounded-full flex items-center justify-center transition-colors">
                <Heart :class="[
                  'h-4 w-4',
                  isInWishlist(product.id) ? 'fill-destructive text-destructive' : 'text-muted-foreground'
                ]" />
              </button>

              <!-- Quick Add -->
              <div
                class="absolute bottom-3 left-3 right-3 opacity-50 group-hover:opacity-100 transition-opacity duration-300">
                <button @click="addToCart(product)" :disabled="!product.in_stock || product.stock_quantity <= 0"
                  :class="[
                    'w-full py-2 rounded-lg transition-normal flex items-center justify-center',
                    product.in_stock && product.stock_quantity > 0
                      ? 'bg-primary hover:bg-primary-hover text-primary-foreground'
                      : 'bg-gray-400 cursor-not-allowed text-gray-700'
                  ]">
                  <ShoppingCart class="h-4 w-4 mr-2" />
                  {{ product.stock_quantity > 0 ? 'Ajouter au panier' : 'Rupture' }}
                </button>
              </div>
            </div>

            <div class="p-4">
              <span v-if="product.category" class="text-xs text-muted-foreground">
                {{ product.category }}
              </span>
              <router-link :to="'/product/' + product.id">
                <h3 class="font-semibold text-foreground hover:text-secondary transition-colors mt-1 line-clamp-2">
                  {{ product.name }}
                </h3>
              </router-link>

              <!-- Rating -->
              <div class="flex items-center gap-1 mt-2" v-if="product.rating > 0">
                <div class="flex">
                  <Star v-for="i in 5" :key="i" :class="[
                    'h-3 w-3',
                    i <= Math.floor(product.rating)
                      ? 'fill-secondary text-secondary'
                      : 'text-muted-foreground/30'
                  ]" />
                </div>
                <span class="text-xs text-muted-foreground">
                  ({{ product.reviews_count || 0 }})
                </span>
              </div>

              <!-- Price -->
              <div class="flex items-center gap-2 mt-2">
                <span class="text-lg font-bold text-foreground">
                  {{ formatPrice(getProductPrice(product)) }}
                </span>
                <span v-if="hasDiscount(product)" class="text-sm text-muted-foreground line-through">
                  {{ formatPrice(product.price) }}
                </span>
              </div>

              <!-- Stock Status -->
              <div v-if="product.stock_quantity <= 10 && product.stock_quantity > 0" class="mt-1">
                <p class="text-xs text-amber-600">
                  Plus que {{ product.stock_quantity }} disponible{{ product.stock_quantity > 1 ? 's' : '' }}
                </p>
              </div>
            </div>
          </div>
        </div>

        <!-- Empty State -->
        <div v-else-if="!loading" class="text-center py-12">
          <Package class="h-16 w-16 text-muted-foreground mx-auto mb-4" />
          <h3 class="text-lg font-semibold text-muted-foreground mb-2">Aucune meilleure vente pour le moment</h3>
        </div>

        <!-- Loading State -->
        <div v-else class="grid grid-cols-2 sm:grid-cols-2 lg:grid-cols-4 gap-2">
          <div v-for="n in 4" :key="n" class="animate-pulse">
            <div class="bg-gray-200 rounded-md h-96"></div>
          </div>
        </div>

        <div v-if="bestsellerProducts.length > 0" class="text-center mt-8">
          <router-link to="/category/bestsellers">
            <button
              class="border border-input bg-background hover:bg-accent hover:text-accent-foreground h-12 px-8 py-2 rounded-md text-lg transition-normal flex items-center justify-center mx-auto">
              Voir toutes les meilleures ventes
              <ArrowRight class="ml-2 h-4 w-4" />
            </button>
          </router-link>
        </div>
      </div>
    </section>

    <!-- New Arrivals -->
    <section v-if="newArrivals.length > 0" class="py-16 bg-muted">
      <div class="container px-2">
        <div class="flex items-center gap-3 mb-8">
          <Sparkles class="h-8 w-8 text-green-500" />
          <div>
            <h2 class="text-2xl md:text-3xl font-bold text-foreground">Nouveautés</h2>
            <p class="text-muted-foreground">Découvrez nos dernières arrivées</p>
          </div>
        </div>

        <div v-if="newArrivals.length > 0" class="grid grid-cols-2 sm:grid-cols-2 lg:grid-cols-4 gap-2">
          <div v-for="product in visibleNewArrivals" :key="product.id"
            class="group hover:shadow-xl transition-all duration-300 border-0 shadow-md overflow-hidden bg-card rounded-md">
            <div class="relative">
              <router-link :to="`/product/${product.category_slug}/${product.slug}`">
                <div class="aspect-square overflow-hidden bg-muted">
                  <img   loading="lazy" :src="getProductImage(product)" :alt="product.name"
                    class="w-full h-full object-cover group-hover:scale-105 transition-transform duration-300"
                    @error="handleImageError" />
                </div>
              </router-link>

              <!-- Badges -->
              <div class="absolute top-3 left-3 flex flex-col gap-2">
                <span v-if="hasDiscount(product)"
                  class="bg-destructive text-destructive-foreground px-2 py-1 rounded-sm text-xs font-semibold">
                  -{{ calculateDiscount(product) }}%
                </span>
                <span v-if="getProductBadge(product)" :class="getBadgeClass(getProductBadge(product))">
                  {{ getProductBadge(product) }}
                </span>
              </div>

              <!-- Wishlist -->
              <button @click="toggleWishlist(product)"
                class="absolute top-3 right-3 w-8 h-8 bg-background/80 hover:bg-background rounded-full flex items-center justify-center transition-colors">
                <Heart :class="[
                  'h-4 w-4',
                  isInWishlist(product.id) ? 'fill-destructive text-destructive' : 'text-muted-foreground'
                ]" />
              </button>

              <!-- Quick Add -->
              <div
                class="absolute bottom-3 left-3 right-3 opacity-50 group-hover:opacity-100 transition-opacity duration-300">
                <button @click="addToCart(product)" :disabled="!product.in_stock || product.stock_quantity <= 0"
                  :class="[
                    'w-full py-2 rounded-lg transition-normal flex items-center justify-center',
                    product.in_stock && product.stock_quantity > 0
                      ? 'bg-primary hover:bg-primary-hover text-primary-foreground'
                      : 'bg-gray-400 cursor-not-allowed text-gray-700'
                  ]">
                  <ShoppingCart class="h-4 w-4 mr-2" />
                  {{ product.stock_quantity > 0 ? 'Ajouter au panier' : 'Rupture' }}
                </button>
              </div>
            </div>

            <div class="p-4">
              <span v-if="product.category" class="text-xs text-muted-foreground">
                {{ product.category }}
              </span>
              <router-link :to="`/product/${product.category_slug}/${product.slug}`">
                <h3 class="font-semibold text-foreground hover:text-secondary transition-colors mt-1 line-clamp-2">
                  {{ product.name }}
                </h3>
              </router-link>

              <!-- Rating -->
              <div class="flex items-center gap-1 mt-2" v-if="product.rating > 0">
                <div class="flex">
                  <Star v-for="i in 5" :key="i" :class="[
                    'h-3 w-3',
                    i <= Math.floor(product.rating)
                      ? 'fill-secondary text-secondary'
                      : 'text-muted-foreground/30'
                  ]" />
                </div>
                <span class="text-xs text-muted-foreground">
                  ({{ product.reviews_count || 0 }})
                </span>
              </div>

              <!-- Price -->
              <div class="flex items-center gap-2 mt-2">
                <span class="text-lg font-bold text-foreground">
                  {{ formatPrice(getProductPrice(product)) }}
                </span>
                <span v-if="hasDiscount(product)" class="text-sm text-muted-foreground line-through">
                  {{ formatPrice(product.price) }}
                </span>
              </div>

              <!-- Stock Status -->
              <div v-if="product.stock_quantity <= 10 && product.stock_quantity > 0" class="mt-1">
                <p class="text-xs text-amber-600">
                  Plus que {{ product.stock_quantity }} disponible{{ product.stock_quantity > 1 ? 's' : '' }}
                </p>
              </div>
            </div>
          </div>
        </div>

        <!-- Empty State -->
        <div v-else-if="!loading" class="text-center py-12">
          <Package class="h-16 w-16 text-muted-foreground mx-auto mb-4" />
          <h3 class="text-lg font-semibold text-muted-foreground mb-2">Aucune nouveauté pour le moment</h3>
        </div>

        <!-- Loading State -->
        <div v-else class="grid grid-cols-2 sm:grid-cols-2 lg:grid-cols-4 gap-2">
          <div v-for="n in 4" :key="n" class="animate-pulse">
            <div class="bg-gray-200 rounded-md h-96"></div>
          </div>
        </div>

        <div v-if="newArrivals.length > 0" class="text-center mt-8">
          <router-link to="/category/new">
            <button
              class="border border-input bg-background hover:bg-accent hover:text-accent-foreground h-12 px-8 py-2 rounded-md text-lg transition-normal flex items-center justify-center mx-auto">
              Voir toutes les nouveautés
              <ArrowRight class="ml-2 h-4 w-4" />
            </button>
          </router-link>
        </div>
      </div>
    </section>

    <!-- Promotions -->
    <section v-if="promoProducts.length > 0" class="py-16 bg-gradient-to-r from-destructive/10 via-background to-destructive/10">
      <div class="container mx-auto px-4">
        <div class="flex items-center gap-3 mb-8">
          <Percent class="h-8 w-8 text-destructive" />
          <div>
            <h2 class="text-2xl md:text-3xl font-bold text-foreground">Promotions</h2>
            <p class="text-muted-foreground">Profitez de nos meilleures offres</p>
          </div>
        </div>

        <div v-if="promoProducts.length > 0" class="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-2">
          <div v-for="product in visiblePromoProducts" :key="product.id"
            class="group hover:shadow-xl transition-all duration-300 border-0 shadow-md overflow-hidden bg-card rounded-md">
            <div class="relative">
              <router-link :to="`/product/${product.category_slug}/${product.slug}`">
                <div class="aspect-square overflow-hidden bg-muted">
                  <img   loading="lazy" :src="getProductImage(product)" :alt="product.name"
                    class="w-full h-full object-cover group-hover:scale-105 transition-transform duration-300"
                    @error="handleImageError" />
                </div>
              </router-link>

              <!-- Badges -->
              <div class="absolute top-3 left-3 flex flex-col gap-2">
                <span v-if="hasDiscount(product)"
                  class="bg-destructive text-destructive-foreground px-2 py-1 rounded-sm text-xs font-semibold">
                  -{{ calculateDiscount(product) }}%
                </span>
                <span v-if="getProductBadge(product)" :class="getBadgeClass(getProductBadge(product))">
                  {{ getProductBadge(product) }}
                </span>
              </div>

              <!-- Wishlist -->
              <button @click="toggleWishlist(product)"
                class="absolute top-3 right-3 w-8 h-8 bg-background/80 hover:bg-background rounded-full flex items-center justify-center transition-colors">
                <Heart :class="[
                  'h-4 w-4',
                  isInWishlist(product.id) ? 'fill-destructive text-destructive' : 'text-muted-foreground'
                ]" />
              </button>

              <!-- Quick Add -->
              <div
                class="absolute bottom-3 left-3 right-3 opacity-50 group-hover:opacity-100 transition-opacity duration-300">
                <button @click="addToCart(product)" :disabled="!product.in_stock || product.stock_quantity <= 0"
                  :class="[
                    'w-full py-2 rounded-lg transition-normal flex items-center justify-center',
                    product.in_stock && product.stock_quantity > 0
                      ? 'bg-primary hover:bg-primary-hover text-primary-foreground'
                      : 'bg-gray-400 cursor-not-allowed text-gray-700'
                  ]">
                  <ShoppingCart class="h-4 w-4 mr-2" />
                  Ajouter au panier
                </button>
              </div>
            </div>

            <div class="p-4">
              <span v-if="product.category" class="text-xs text-muted-foreground">
                {{ product.category }}
              </span>
              <router-link :to="`/product/${product.category_slug}/${product.slug}`">
                <h3 class="font-semibold text-foreground hover:text-secondary transition-colors mt-1 line-clamp-2">
                  {{ product.name }}
                </h3>
              </router-link>

              <!-- Rating -->
              <div class="flex items-center gap-1 mt-2" v-if="product.rating > 0">
                <div class="flex">
                  <Star v-for="i in 5" :key="i" :class="[
                    'h-3 w-3',
                    i <= Math.floor(product.rating)
                      ? 'fill-secondary text-secondary'
                      : 'text-muted-foreground/30'
                  ]" />
                </div>
                <span class="text-xs text-muted-foreground">
                  ({{ product.reviews_count || 0 }})
                </span>
              </div>

              <!-- Price -->
              <div class="flex items-center gap-2 mt-2">
                <span class="text-lg font-bold text-foreground">
                  {{ formatPrice(getProductPrice(product)) }}
                </span>
                <span v-if="hasDiscount(product)" class="text-sm text-muted-foreground line-through">
                  {{ formatPrice(product.price) }}
                </span>
              </div>

              <!-- Stock Status -->
              <div v-if="product.stock_quantity <= 10 && product.stock_quantity > 0" class="mt-1">
                <p class="text-xs text-amber-600">
                  Plus que {{ product.stock_quantity }} disponible{{ product.stock_quantity > 1 ? 's' : '' }}
                </p>
              </div>
            </div>
          </div>
        </div>

        <!-- Empty State -->
        <div v-else-if="!loading" class="text-center py-12">
          <Percent class="h-16 w-16 text-muted-foreground mx-auto mb-4" />
          <h3 class="text-lg font-semibold text-muted-foreground mb-2">Aucune promotion pour le moment</h3>
          <p class="text-sm text-muted-foreground">Revenez bientôt pour découvrir nos offres spéciales</p>
        </div>

        <!-- Loading State -->
        <div v-else class="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-2">
          <div v-for="n in 4" :key="n" class="animate-pulse">
            <div class="bg-gray-200 rounded-md h-96"></div>
          </div>
        </div>

        <div v-if="promoProducts.length > 0" class="text-center mt-8">
          <router-link to="/category/promos">
            <button
              class="bg-destructive hover:bg-destructive/90 text-destructive-foreground h-12 px-8 py-2 rounded-md text-lg transition-normal flex items-center justify-center mx-auto">
              Voir toutes les promotions
              <ArrowRight class="ml-2 h-4 w-4" />
            </button>
          </router-link>
        </div>
      </div>
    </section>

    <!-- Advantages -->
    <section class="py-16 bg-primary text-primary-foreground">
      <div class="container mx-auto px-4">
        <div class="text-center mb-12">
          <h2 class="text-2xl md:text-3xl font-bold mb-4">Pourquoi nous choisir ?</h2>
          <p class="text-primary-foreground/80">Des avantages exclusifs pour nos clients</p>
        </div>

        <div class="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-8">
          <div v-for="(advantage, index) in advantages" :key="index" class="text-center">
            <div class="w-16 h-16 mx-auto mb-4 rounded-full bg-secondary/20 flex items-center justify-center">
              <component :is="advantage.icon" class="h-8 w-8 text-secondary" />
            </div>
            <h3 class="text-lg font-semibold mb-2">{{ advantage.title }}</h3>
            <p class="text-primary-foreground/70 text-sm">{{ advantage.description }}</p>
          </div>
        </div>
      </div>
    </section>

    <!-- CTA Banner -->
    <section class="py-16 bg-muted">
      <div class="container mx-auto px-4">
        <div
          class="bg-gradient-to-r from-secondary via-secondary-hover to-secondary rounded-2xl p-8 md:p-12 text-center">
          <h2 class="text-2xl md:text-4xl font-bold text-secondary-foreground mb-4">
            Inscrivez-vous à notre newsletter
          </h2>
          <p class="text-secondary-foreground/80 mb-6 max-w-xl mx-auto">
            Recevez nos offres exclusives et les dernières tendances directement dans votre boîte mail
          </p>
          <div class="flex flex-col sm:flex-row gap-4 justify-center max-w-md mx-auto">
            <input type="email" placeholder="Votre adresse email"
              class="flex-1 px-4 py-3 rounded-lg border-0 focus:ring-2 focus:ring-primary text-foreground"
              v-model="email" @keyup.enter="subscribeNewsletter" />
            <button
              class="bg-primary hover:bg-primary-hover text-primary-foreground px-8 py-3 rounded-lg transition-normal"
              @click="subscribeNewsletter">
              S'inscrire
            </button>
          </div>
        </div>
      </div>
    </section>
  </div>
</template>

<script>
import { 
  ArrowRight, 
  Truck, 
  Shield, 
  Headphones, 
  RefreshCw,
  Star,
  TrendingUp,
  Sparkles,
  Percent,
  ShoppingBag,
  Shirt,
  Watch,
  Smartphone,
  Sparkle,
  Home,
  Dumbbell,
  Package,
  Heart,
  ShoppingCart,
  ChevronLeft,
  ChevronRight
} from 'lucide-vue-next';

export default {
  name: 'HomePage',
  components: {
    ArrowRight,
    Truck,
    Shield,
    Headphones,
    RefreshCw,
    Star,
    TrendingUp,
    Sparkles,
    Percent,
    ShoppingBag,
    Shirt,
    Watch,
    Smartphone,
    Sparkle,
    Home,
    Dumbbell,
    Package,
    Heart,
    ShoppingCart,
    ChevronLeft,
    ChevronRight
  },
  props: ['getdevice'],
  data() {
    return {
      // Hero Section
      currentSlide: 0,
      isAutoPlaying: true,
      visibleCards: 2,
      email: '',
      interval: null,
      
      // Navigation indices
      trendingIndex: 0,
      bestsellerIndex: 0,
      newArrivalIndex: 0,
      promoIndex: 0,
      
      // Loading state
      loading: false,
      
      // Stats - calculées à partir des données du store
      stats: {
        customers: 1000,
        products: 0,
        orders: 2500
      },
      
      // Hero background image
      heroBackground: require('@/assets/hero-bg.jpg'),
      
      // Default product image
      defaultProductImage: require('@/assets/default-product.png'),
      
      // Advantages (unchanged)
      advantages: [
        { icon: 'Truck', title: 'Livraison Rapide', description: 'Livraison en 24-48h partout au Cameroun' },
        { icon: 'Shield', title: 'Paiement Sécurisé', description: 'Transactions 100% sécurisées' },
        { icon: 'Headphones', title: 'Support Client', description: 'Assistance 7j/7 par téléphone et WhatsApp' },
        { icon: 'RefreshCw', title: 'Garantie Satisfaction', description: 'Retour gratuit sous 14 jours' }
      ]
    };
  },
  computed: {
    // Get data from Vuex store - using your existing store structure
    productnumber() {
      return this.$store.state.productnumber || 0;
    },
    categories() {
      return this.$store.state.categories || [];
    },
    products() {
     
      return this.$store.state.allProducts || [];
    },
    
    // Hero Promo Products - from your store state
    heroPromoProducts() {
      return this.$store.state.heroPromoProducts || [];
    },
    
    // Trending Products - from your store state
    trendingProducts() {
      return this.$store.state.trendingProducts || [];
    },
    
    // Bestseller Products - from your store state
    bestsellerProducts() {
      return this.$store.state.bestsellerProducts || [];
    },
    
    // New Arrivals - from your store state
    newArrivals() {
      return this.$store.state.newArrivals || [];
    },
    
    // Promo Products - from your store state
    promoProducts() {
      return this.$store.state.promoProducts || [];
    },
    
    // Wishlist - from your store state
    wishlist() {
      return this.$store.state.wishlists || [];
    },
    
    // Cart - from your store state
    cart() {
      return this.$store.state.cart || [];
    },
    
    // Navigation computed properties
    maxSlide() {
      return Math.max(0, this.heroPromoProducts.length - this.visibleCards);
    },
    
    slideIndicators() {
      return Math.min(this.maxSlide + 1, 5);
    },
    
    visibleTrendingProducts() {
      const start = this.trendingIndex;
      const end = start + 4;
      return this.trendingProducts.slice(start, end);
    },
    
    visibleBestsellerProducts() {
      const start = this.bestsellerIndex;
      const end = start + 4;
      return this.bestsellerProducts.slice(start, end);
    },
    
    visibleNewArrivals() {
      const start = this.newArrivalIndex;
      const end = start + 4;
      return this.newArrivals.slice(start, end);
    },
    
    visiblePromoProducts() {
      const start = this.promoIndex;
      const end = start + 4;
      return this.promoProducts.slice(start, end);
    },
    
    isAuthenticated() {
      return this.$store.state.token !== null;
    }
  },
  methods: {
    // Image handling - adapté pour votre nouvelle structure
    getProductImage(product) {
      if (product.image) {
        // Utiliser l'URL complète depuis votre store
        return `${this.$store.state.baseUrl}${product.image}`;
      }
      
      // Image par défaut
      return this.defaultProductImage;
    },
    
    handleImageError(event) {
      event.target.src = this.defaultProductImage;
    },
    
    // Price formatting
    formatPrice(price) {
      if (!price && price !== 0) return '0 FCFA';
      const priceNumber = parseFloat(price) || 0;
      return new Intl.NumberFormat('fr-FR').format(priceNumber) + ' FCFA';
    },
    
    getProductPrice(product) {
      // Utiliser promo_price si disponible, sinon price
      const promoPrice = parseFloat(product.promo_price);
      const price = parseFloat(product.price);
      return promoPrice || price || 0;
    },
    
    getPromoPrice(product) {
      return this.getProductPrice(product);
    },
    
    // Vérifier si le produit a une remise
    hasDiscount(product) {
      const price = parseFloat(product.price) || 0;
      const promoPrice = parseFloat(product.promo_price) || 0;
      return promoPrice > 0 && promoPrice < price;
    },
    
    // Calculer le pourcentage de remise
    calculateDiscount(product) {
      if (this.hasDiscount(product)) {
        const price = parseFloat(product.price) || 0;
        const promoPrice = parseFloat(product.promo_price) || 0;
        const discount = ((price - promoPrice) / price) * 100;
        return Math.round(discount);
      }
      return 0;
    },
    
    // Badge handling adapté
    getProductBadge(product) {
      // Vérifier la remise
      if (this.hasDiscount(product)) {
        return 'Promotion';
      }
      
      // Vérifier si c'est nouveau (créé il y a moins de 30 jours)
      if (product.created_at) {
        const thirtyDaysAgo = new Date();
        thirtyDaysAgo.setDate(thirtyDaysAgo.getDate() - 30);
        const createdDate = new Date(product.created_at);
        if (createdDate > thirtyDaysAgo) {
          return 'Nouveau';
        }
      }
      
      // Vérifier si c'est tendance (beaucoup de vues)
      if (product.view_count > 100) {
        return 'Tendance';
      }
      
      // Vérifier la note
      if (product.rating >= 4.5) {
        return 'Best Seller';
      }
      
      return null;
    },
    
    getBadgeClass(badge) {
      const badgeClasses = {
        'Tendance': 'bg-orange-500 text-white px-2 py-1 rounded-sm text-xs font-medium',
        'Best Seller': 'bg-secondary text-secondary-foreground px-2 py-1 rounded-sm text-xs font-medium',
        'Nouveau': 'bg-green-500 text-white px-2 py-1 rounded-sm text-xs font-medium',
        'Promotion': 'bg-destructive text-destructive-foreground px-2 py-1 rounded-sm text-xs font-medium',
        'default': 'bg-primary text-primary-foreground px-2 py-1 rounded-sm text-xs font-medium'
      };
      
      return badgeClasses[badge] || badgeClasses.default;
    },
    
    // Category handling
    getCategoryIcon(category) {
      const iconMap = {
        'Chaussures': ShoppingBag,
        'Vêtements': Shirt,
        'Accessoires': Watch,
        'Électronique': Smartphone,
        'Beauté': Sparkle,
        'Maison': Home,
        'Sport': Dumbbell,
        'default': Package
      };
      
      return iconMap[category.name] || iconMap.default;
    },
    
    getCategoryColor(category) {
      const colorMap = {
        'Chaussures': 'from-amber-500 to-orange-600',
        'Vêtements': 'from-blue-500 to-indigo-600',
        'Accessoires': 'from-pink-500 to-rose-600',
        'Électronique': 'from-cyan-500 to-teal-600',
        'Beauté': 'from-purple-500 to-violet-600',
        'Maison': 'from-green-500 to-emerald-600',
        'Sport': 'from-red-500 to-rose-600',
        'default': 'from-slate-500 to-gray-600'
      };
      
      return colorMap[category.name] || colorMap.default;
    },
    
    // Hero slider methods
    nextSlide() {
      this.isAutoPlaying = false;
      if (this.currentSlide >= this.maxSlide) {
        this.currentSlide = 0;
      } else {
        this.currentSlide = this.currentSlide + 1;
      }
    },
    
    prevSlide() {
      this.isAutoPlaying = false;
      if (this.currentSlide <= 0) {
        this.currentSlide = this.maxSlide;
      } else {
        this.currentSlide = this.currentSlide - 1;
      }
    },
    
    goToSlide(index) {
      this.isAutoPlaying = false;
      this.currentSlide = index;
    },
    
    // Navigation methods
    nextTrendingIndex() {
      if (this.trendingProducts.length > 4) {
        this.trendingIndex = Math.min(this.trendingProducts.length - 4, this.trendingIndex + 1);
      }
    },
    
    prevTrendingIndex() {
      this.trendingIndex = Math.max(0, this.trendingIndex - 1);
    },
    
    nextBestsellerIndex() {
      if (this.bestsellerProducts.length > 4) {
        this.bestsellerIndex = Math.min(this.bestsellerProducts.length - 4, this.bestsellerIndex + 1);
      }
    },
    
    prevBestsellerIndex() {
      this.bestsellerIndex = Math.max(0, this.bestsellerIndex - 1);
    },
    
    nextNewArrivalIndex() {
      if (this.newArrivals.length > 4) {
        this.newArrivalIndex = Math.min(this.newArrivals.length - 4, this.newArrivalIndex + 1);
      }
    },
    
    prevNewArrivalIndex() {
      this.newArrivalIndex = Math.max(0, this.newArrivalIndex - 1);
    },
    
    nextPromoIndex() {
      if (this.promoProducts.length > 4) {
        this.promoIndex = Math.min(this.promoProducts.length - 4, this.promoIndex + 1);
      }
    },
    
    prevPromoIndex() {
      this.promoIndex = Math.max(0, this.promoIndex - 1);
    },
    
    // Wishlist methods
    toggleWishlist(product) {
      if (!this.isAuthenticated) {
        this.$router.push('/auth');
        return;
      }
      
      if (this.isInWishlist(product.id)) {
        this.$store.dispatch('removeFromWishlist', product.id);
      } else {
        this.$store.dispatch('addToWishlist', product);
      }
    },
    
    isInWishlist(productId) {
      return this.wishlist.some(item => item.product_id === productId);
    },
    
    // Cart methods
    async addToCart(product) {
      if (!this.isAuthenticated) {
        this.$router.push('/auth');
        return;
      }
      
      if (!product.in_stock || product.stock_quantity <= 0) {
        alert('Ce produit n\'est pas disponible');
        return;
      }
      
      try {
        const cartData = {
          id: product.id,
          quantity: 1,
          price: this.getProductPrice(product)
        };
        
        await this.$store.dispatch('addToCart', cartData);
        
        this.showNotification('Produit ajouté au panier');
      } catch (error) {
        console.error('Error adding to cart:', error);
        if (error.response && error.response.data.error) {
          alert(error.response.data.error);
        } else {
          alert('Erreur lors de l\'ajout au panier');
        }
      }
    },
    
    showNotification(message) {
      // Simple notification
      // alert(message);
    },
    
    // Newsletter
    subscribeNewsletter() {
      if (this.email) {
        console.log('Subscribed with email:', this.email);
        alert('Merci de votre inscription à notre newsletter !');
        this.email = '';
      } else {
        alert('Veuillez entrer votre adresse email');
      }
    },
    
    // Calculate stats from store data
    calculateStats() {
     
      
      // Calculer le nombre de commandes à partir du store
      this.stats.orders = this.$store.state.orders ? this.$store.state.orders.length : 0;
    }
  },
  watch: {
    // Mettre à jour les stats quand les données changent
    trendingProducts() {
      this.calculateStats();
    },
    bestsellerProducts() {
      this.calculateStats();
    },
    newArrivals() {
      this.calculateStats();
    },
    promoProducts() {
      this.calculateStats();
    }
  },
  async mounted() {
    
    this.calculateStats();
    
    // Démarrer le slider automatique
    this.interval = setInterval(() => {
      if (this.isAutoPlaying && this.heroPromoProducts.length > this.visibleCards) {
        this.nextSlide();
      }
    }, 3000);
  },
  unmounted() {
    if (this.interval) {
      clearInterval(this.interval);
    }
  }
};
</script>

<style scoped>
.line-clamp-1 {
  overflow: hidden;
  display: -webkit-box;
  -webkit-box-orient: vertical;
  -webkit-line-clamp: 1;
}

.line-clamp-2 {
  overflow: hidden;
  display: -webkit-box;
  -webkit-box-orient: vertical;
  -webkit-line-clamp: 2;
}

.shadow-glow {
  box-shadow: 0 0 20px rgba(59, 130, 246, 0.5);
}

.transition-normal {
  transition: all 0.2s ease-in-out;
}

/* Animation pour les indicateurs de slide */
@keyframes slide-in {
  from {
    opacity: 0;
    transform: translateX(-20px);
  }

  to {
    opacity: 1;
    transform: translateX(0);
  }
}

.slide-enter-active {
  animation: slide-in 0.3s ease-out;
}

/* Styles pour les états de chargement */
.animate-pulse {
  animation: pulse 2s cubic-bezier(0.4, 0, 0.6, 1) infinite;
}

@keyframes pulse {

  0%,
  100% {
    opacity: 1;
  }

  50% {
    opacity: 0.5;
  }
}

/* Améliorations responsive */
@media (max-width: 640px) {
  /* .grid-cols-2 {
    grid-template-columns: repeat(2, minmax(0, 1fr));
  } */

  .aspect-square {
    aspect-ratio: 1/1;
  }
}

/* @media (min-width: 768px) {
  .grid-cols-2 {
    grid-template-columns: repeat(2, minmax(0, 1fr));
  }
}

@media (min-width: 1024px) {
  .grid-cols-4 {
    grid-template-columns: repeat(4, minmax(0, 1fr));
  }
} */
</style>
